import { Group, Modal, Stack } from "@mantine/core"
import { Button, ErrorBox, Loader } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  findTraceabilityJobsByStatusHttpControllerFindTraceabilityJobsByStatusPathParamsStatusEnum as TraceabilityJobStatusEnum,
  useFindTraceabilityJobsByStatus,
  useGetTraceabilityCampaignStats,
  useLaunchTraceability,
  VerifyRawOperationsFileResponseDto,
} from "~/gen/api"
import { LaunchTraceabilityModalHeader } from "~/pages/Brand/Products/List/Components/LaunchTraceabilityModal/LaunchTraceabilityModalHeader"
import { TraceabilityStatsInfoBoxes } from "~/pages/Brand/Products/List/Components/LaunchTraceabilityModal/TraceabilityStatsInfoBoxes"
import {
  RawOperationsFileError,
  formatVerifyRawOperationsFileError,
} from "~/pages/Brand/Products/List/Components/LaunchTraceabilityModal/utils"
import { useVerifyRawOperationsFileMutation } from "~/pages/Brand/Products/List/hooks"

type Props = {
  opened: boolean
  cancelModalOpened: boolean
  onClose: () => void
  onSuccess: () => void
  onCancel: () => void
}

export const LaunchTraceabilityModal = ({
  opened,
  cancelModalOpened,
  onClose,
  onSuccess,
  onCancel,
}: Props) => {
  const { data: stats, isLoading } = useGetTraceabilityCampaignStats()

  const { t } = useTranslation(["traceability", "common"])

  const [isLaunchTraceabilityLoading, setIsLaunchTraceabilityLoading] =
    useState<boolean>(false)

  const [verifyRawOperationsFileErrors, setVerifyRawOperationsFileErrors] =
    useState<RawOperationsFileError[]>([])

  const [isRawOperationsFileLoading, setIsRawOperationsFileLoading] =
    useState<boolean>(false)

  const [rawOperationsUploadedFile, setRawOperationsUploadedFile] = useState<
    VerifyRawOperationsFileResponseDto | undefined
  >()

  const [rawOperationsChecked, setRawOperationsChecked] =
    useState<boolean>(false)

  const { mutate: verifyRawOperationsFile } =
    useVerifyRawOperationsFileMutation({
      onError(error) {
        setVerifyRawOperationsFileErrors(
          formatVerifyRawOperationsFileError(error)
        )
      },
      onSuccess(data) {
        setVerifyRawOperationsFileErrors([])
        setRawOperationsUploadedFile(data)
      },
      onSettled() {
        setIsRawOperationsFileLoading(false)
      },
    })

  const { mutate: launchTraceability } = useLaunchTraceability({
    mutation: {
      onSuccess,
      onSettled() {
        setIsLaunchTraceabilityLoading(false)
        onClose()
      },
    },
  })

  const { data: launchTraceabilityJobs } = useFindTraceabilityJobsByStatus(
    TraceabilityJobStatusEnum.RUNNING
  )

  const isTraceabilityJobRunning = useMemo<boolean>(() => {
    return (launchTraceabilityJobs?.data.count ?? 0) > 0
  }, [launchTraceabilityJobs])

  const shouldShowCancelButton = useMemo<boolean>(() => {
    if (!stats || isTraceabilityJobRunning) return false
    return stats.data.eligibleProductsCount > 0
  }, [stats, isTraceabilityJobRunning])

  const shouldShowLaunchTraceabilityButton = useMemo<boolean>(() => {
    if (!stats || isTraceabilityJobRunning) return false
    return stats.data.eligibleProductsCount > 0
  }, [stats, isTraceabilityJobRunning])

  const launchTraceabilityButtonLabel = useMemo<string>(() => {
    if (stats && stats.data.eligibleProductsCount > 1) {
      return t(
        "traceability:launch.modal.eligibleProducts.launchButton.label.multiple",
        {
          eligibleProductsCount: stats.data.eligibleProductsCount,
        }
      )
    }

    if (stats && stats.data.eligibleProductsCount === 1) {
      return t(
        "traceability:launch.modal.eligibleProducts.launchButton.label.single"
      )
    }

    return t(
      "traceability:launch.modal.eligibleProducts.launchButton.label.none"
    )
  }, [stats, t])

  if (isLoading) {
    return <Loader size={16} />
  }

  if (!stats) {
    return <ErrorBox title={t("common:error.default")} error={null} />
  }

  return (
    <Modal
      opened={opened}
      onClose={shouldShowCancelButton ? onCancel : onClose}
      size="lg"
      {...(cancelModalOpened ? { display: "none" } : {})}
    >
      <Stack gap="xs">
        <LaunchTraceabilityModalHeader
          tracedProductsCount={stats.data.tracedProductsCount}
          totalProducts={stats.data.totalProducts}
          currentTraceabilityCoverage={
            stats.data.estimatedTraceabilityCoverage.current
          }
          isTraceabilityJobRunning={isTraceabilityJobRunning}
        />
        {!isTraceabilityJobRunning ? (
          <TraceabilityStatsInfoBoxes
            stats={stats.data}
            onFileChange={(file) => {
              setIsRawOperationsFileLoading(true)
              verifyRawOperationsFile({ file })
            }}
            rawOperationsFileErrors={verifyRawOperationsFileErrors}
            isRawOperationsFileLoading={isRawOperationsFileLoading}
            setRawOperationsFileErrors={setVerifyRawOperationsFileErrors}
            setRawOperationsChecked={setRawOperationsChecked}
            rawOperationsChecked={rawOperationsChecked}
            rawOperationsUploadedFile={rawOperationsUploadedFile}
            setRawOperationsUploadedFile={setRawOperationsUploadedFile}
          />
        ) : null}
        <Group grow w="100%">
          {shouldShowLaunchTraceabilityButton ? (
            <Button
              fullWidth
              size="sm"
              onClick={() => {
                setIsLaunchTraceabilityLoading(true)
                launchTraceability({
                  data: {
                    filePath: rawOperationsUploadedFile?.filePath,
                    fileOriginalName:
                      rawOperationsUploadedFile?.fileOriginalName,
                  },
                })
              }}
              loading={
                isLaunchTraceabilityLoading || isRawOperationsFileLoading
              }
              disabled={
                isLaunchTraceabilityLoading ||
                isRawOperationsFileLoading ||
                verifyRawOperationsFileErrors.length > 0 ||
                (rawOperationsChecked && !rawOperationsUploadedFile)
              }
            >
              {launchTraceabilityButtonLabel}
            </Button>
          ) : null}
          {shouldShowCancelButton ? (
            <Button
              fullWidth
              variant="secondary"
              onClick={onCancel}
              disabled={
                isLaunchTraceabilityLoading || isRawOperationsFileLoading
              }
            >
              {t("launch.modal.cancel.button")}
            </Button>
          ) : (
            <Button fullWidth variant="primary" onClick={onClose} size="sm">
              {t("launch.modal.close.button")}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  )
}
